import { logging } from '@js-sdk/debug-tools';
import { EventEmitter } from '@spotify-internal/emitter';
import { Errors } from '../../../enums/errors';
import { Event } from '../../../enums/event';
import { EMEError } from '../../../error/eme';
import { emitEncrypted } from './emit_encrypted';
import { defer } from '../../defer';
import { LicenseRequestEvent, } from './license_request_event';
const debugLogger = logging.forTag('eme_polyfill.safari');
function toUTF16(str) {
    const result = new Uint8Array(str.length * 2);
    const view = new DataView(result.buffer);
    const strArray = str.split('');
    for (let i = 0, l = strArray.length; i < l; i++) {
        const item = strArray[i];
        const value = item.charCodeAt(0);
        view.setUint16(i * 2, value, true);
    }
    return result.buffer;
}
function fromUTF16(data) {
    const length = Math.floor(data.byteLength / 2);
    const arr = new Uint16Array(length);
    const dataView = new DataView(data.buffer);
    for (let i = 0; i < length; i++) {
        arr[i] = dataView.getUint16(i * 2, true);
    }
    return String.fromCharCode.apply(null, arr);
}
function _setMediaKeys(mediaKeys) {
    // Cast mediaKeys to any as it could possibly different.
    const oldKeys = this.mediaKeys;
    if (oldKeys && oldKeys !== mediaKeys) {
        oldKeys.detach(this);
    }
    delete this['mediaKeys']; // eslint-disable-line dot-notation
    this.mediaKeys = mediaKeys;
    if (mediaKeys) {
        return mediaKeys.attach(this);
    }
    return Promise.resolve();
}
class MediaKeySession extends EventEmitter {
    constructor(nativeMediaKeys, certificate) {
        super();
        this._attached = false;
        this._nativeKeySession = null;
        /**
         * The last deferred created for the last `update` call.
         */
        this._lastGenerateResolver = defer();
        /**
         * The last deferred created for the last `generateRequest` call.
         */
        this._lastUpdateResolver = defer();
        // EventTarget mocking
        this.addEventListener = this.addListener;
        this.removeEventListener = this.removeListener;
        this._nativeMediaKeys = nativeMediaKeys;
        this._serverCertificate = certificate;
        this._onKeyMessage = this._onKeyMessage.bind(this);
        this._onKeyAdded = this._onKeyAdded.bind(this);
        this._onKeyError = this._onKeyError.bind(this);
    }
    _attach() {
        if (this._attached) {
            return;
        }
        const nativeKeySession = this._nativeKeySession;
        if (!nativeKeySession) {
            debugLogger.warn('Tried to attach with no WebKitMediaKeySession');
            return;
        }
        nativeKeySession.addEventListener(Event.WEBKIT_KEY_MESSAGE, this._onKeyMessage);
        nativeKeySession.addEventListener(Event.WEBKIT_KEY_ADDED, this._onKeyAdded);
        nativeKeySession.addEventListener(Event.WEBKIT_KEY_ERROR, this._onKeyError);
    }
    _detach() {
        if (!this._attached) {
            return;
        }
        const nativeKeySession = this._nativeKeySession;
        if (!nativeKeySession) {
            debugLogger.warn('Tried to detach with no WebKitMediaKeySession');
            return;
        }
        nativeKeySession.removeEventListener(Event.WEBKIT_KEY_MESSAGE, this._onKeyMessage);
        nativeKeySession.removeEventListener(Event.WEBKIT_KEY_ADDED, this._onKeyAdded);
        nativeKeySession.removeEventListener(Event.WEBKIT_KEY_ERROR, this._onKeyError);
    }
    _onKeyMessage(ev) {
        if (!ev.message || !ev.message.buffer) {
            return;
        }
        this.emitEvent(new LicenseRequestEvent(ev.message.buffer));
        if (this._lastGenerateResolver) {
            this._lastGenerateResolver.resolve(true);
            this._lastGenerateResolver = null;
        }
    }
    _onKeyAdded() {
        if (this._lastUpdateResolver) {
            this._lastUpdateResolver.resolve(true);
            this._lastUpdateResolver = null;
        }
        this.emit(Event.KEY_SESSION_STATUSES_CHANGE, null);
    }
    _onKeyError() {
        const nativeError = this._nativeKeySession && this._nativeKeySession.error;
        const error = new EMEError(Errors.EME_MEDIA_KEY_SESSION_SAFARI_ERROR, (nativeError && nativeError.message) || 'MediaKeySession Safari Error');
        if (nativeError) {
            error.debug.errorCode = nativeError.code;
            error.debug.systemCode = nativeError.systemCode;
        }
        if (this._lastGenerateResolver) {
            this._lastGenerateResolver.reject(error);
            this._lastGenerateResolver = null;
        }
        else if (this._lastUpdateResolver) {
            this._lastUpdateResolver.reject(error);
            this._lastUpdateResolver = null;
        }
    }
    _rebuildInitData(initDataBuffer) {
        const intLength = 4;
        const initDataArray = new Uint8Array(initDataBuffer);
        const dataview = new DataView(initDataArray.buffer);
        const length = dataview.getUint32(0, true);
        if (length + intLength !== initDataArray.byteLength) {
            throw new EMEError(Errors.EME_INIT_DATA_MALFORMED, 'Malformed init data');
        }
        // UTF16 URI in the format `skd://<hex-content-id>`. As we know the format of
        // this URI, we will parse it directly rather than using a URL parser.
        const uriString = fromUTF16(initDataArray.slice(intLength));
        const matches = uriString.match(/^skd:\/\/([0-9a-fA-F]+)/);
        if (!matches) {
            throw new EMEError(Errors.EME_INIT_DATA_MALFORMED, 'Invalid content ID');
        }
        const contentId = matches[1];
        // Actual init data format:
        // - Raw init data
        // - 4-byte little-endian length of the content ID
        // - Content ID in little-endian UTF16
        // - 4-byte little-endian length of the certificate
        // - Certificate as little-endian UTF8
        const contentIdArray = new Uint8Array(toUTF16(contentId));
        const serverCertificate = this._serverCertificate;
        const rebuiltInitData = new Uint8Array(initDataArray.byteLength +
            intLength +
            contentIdArray.byteLength +
            intLength +
            serverCertificate.byteLength);
        let offset = 0;
        // Original init data
        rebuiltInitData.set(initDataArray, offset);
        offset += initDataArray.byteLength;
        const view = new DataView(rebuiltInitData.buffer);
        // Content ID
        view.setUint32(offset, contentIdArray.byteLength, true);
        offset += intLength;
        rebuiltInitData.set(contentIdArray, offset);
        offset += contentIdArray.byteLength;
        // Server Certificate
        view.setUint32(offset, serverCertificate.byteLength, true);
        offset += intLength;
        rebuiltInitData.set(serverCertificate, offset);
        return rebuiltInitData;
    }
    generateRequest(_initData, initDataBuffer) {
        return new Promise((resolve, reject) => {
            this._lastGenerateResolver = {
                resolve: resolve,
                reject: reject,
            };
            try {
                const initData = this._rebuildInitData(initDataBuffer);
                this._nativeKeySession = this._nativeMediaKeys.createSession('video/mp4', new Uint8Array(initData), null);
                this._attach();
            }
            catch (e) {
                this._lastGenerateResolver = null;
                reject(e);
            }
        });
    }
    update(license) {
        return new Promise((resolve, reject) => {
            this._lastUpdateResolver = {
                resolve: resolve,
                reject: reject,
            };
            if (!this._nativeKeySession) {
                debugLogger.warn('Tried to update with no WebKitMediaKeySession');
                return;
            }
            try {
                this._nativeKeySession.update(new Uint8Array(license));
            }
            catch (e) {
                reject(e);
            }
        });
    }
    close() {
        return new Promise((resolve) => {
            this._detach();
            resolve(true);
        });
    }
}
class MediaKeys {
    constructor(keySystem) {
        this._lastBoundSetter = null;
        this._serverCertificate = null;
        this.shouldRefreshPerTrack = true;
        this._nativeMediaKeys = new WebKitMediaKeys(keySystem);
    }
    attach(element) {
        return new Promise((resolve) => {
            if (element.readyState >= 1) {
                element.webkitSetMediaKeys(this._nativeMediaKeys);
                resolve();
                return;
            }
            const setter = () => {
                element.removeEventListener(Event.MEDIA_LOADEDMETADATA, setter);
                this._lastBoundSetter = null;
                element.webkitSetMediaKeys(this._nativeMediaKeys);
            };
            this._lastBoundSetter = setter;
            element.addEventListener(Event.MEDIA_LOADEDMETADATA, setter);
            element.addEventListener(Event.WEBKIT_NEEDKEY, emitEncrypted);
            resolve();
        });
    }
    detach(element) {
        element.removeEventListener(Event.WEBKIT_NEEDKEY, emitEncrypted);
        if (!this._lastBoundSetter) {
            return;
        }
        element.removeEventListener(Event.MEDIA_LOADEDMETADATA, this._lastBoundSetter);
        this._lastBoundSetter = null;
    }
    setServerCertificate(certificate) {
        this._serverCertificate = new Uint8Array(certificate);
        return Promise.resolve(true);
    }
    createSession() {
        if (!this._serverCertificate) {
            throw new EMEError(Errors.EME_INVALID_STATE_ERROR, 'The server certificate is not available');
        }
        return new MediaKeySession(this._nativeMediaKeys, this._serverCertificate);
    }
}
class MediaKeySystemAccess {
    constructor(keySystem, configs) {
        this.keySystem = keySystem;
        const configuration = this._checkConfig(configs);
        if (!configuration) {
            throw new EMEError(Errors.EME_NO_SUPPORTED_CONFIGURATION, 'No supported configurations');
        }
        this._configuration = configuration;
    }
    _checkConfig(configs) {
        const keySystem = this.keySystem;
        let hasSupported = false;
        for (const config of configs) {
            const configuration = {
                initDataTypes: config.initDataTypes,
                audioCapabilities: [],
                videoCapabilities: [],
                persistentState: 'optional',
                distinctiveIdentifier: 'optional',
                sessionTypes: ['temporary'],
            };
            // Audio caps
            if (config.audioCapabilities) {
                for (const cap of config.audioCapabilities) {
                    if (!cap.contentType) {
                        continue;
                    }
                    const contentType = cap.contentType.split(';')[0];
                    if (WebKitMediaKeys.isTypeSupported(keySystem, contentType)) {
                        configuration.audioCapabilities.push(cap);
                        hasSupported = true;
                    }
                }
            }
            // Video caps
            if (config.videoCapabilities) {
                for (const cap of config.videoCapabilities) {
                    if (!cap.contentType) {
                        continue;
                    }
                    const contentType = cap.contentType.split(';')[0];
                    if (WebKitMediaKeys.isTypeSupported(keySystem, contentType)) {
                        configuration.videoCapabilities.push(cap);
                        hasSupported = true;
                    }
                }
            }
            if (hasSupported) {
                return configuration;
            }
        }
        return null;
    }
    getConfiguration() {
        return this._configuration;
    }
    createMediaKeys() {
        return new Promise((resolve) => {
            resolve(new MediaKeys(this.keySystem));
        });
    }
}
export function setup() {
    // Polyfill `navigator.requestMediaKeySystemAccess`
    navigator.requestMediaKeySystemAccess = function (ks, conf) {
        return new Promise((resolve) => {
            resolve(new MediaKeySystemAccess(ks, conf));
        });
    };
    // Polyfill `HTMLMediaElement.prototype.setMediaKeys`
    const elementProto = HTMLMediaElement.prototype;
    delete elementProto['mediaKeys']; // eslint-disable-line dot-notation
    elementProto.setMediaKeys = _setMediaKeys;
}
//# sourceMappingURL=safari.js.map